// ═══════════════════════════════════════════════════════════════
// BillingEnforcement.js — Pure logic functions, no Stripe dependency
// Used by UI components to gate features based on plan/subscription.
// ═══════════════════════════════════════════════════════════════

import { PLAN_LIMITS, getPlanLimits, DEFAULT_CONFIG } from './BillingConfig.js';

// A plan name alone does not establish an active entitlement.
export function hasCurrentMembership(userBilling, tenant, now = Date.now()) {
  const isOrg = tenant?.id !== 'tenant-public' && !!tenant?.plan;
  const status = isOrg ? tenant.status : userBilling?.planStatus;
  if (status === 'active') return true;
  if (status !== 'trialing') return false;
  const endsAt = isOrg ? tenant.trialExpiresAt : userBilling?.trialEndsAt;
  return !!endsAt && Number.isFinite(new Date(endsAt).getTime()) && new Date(endsAt).getTime() > now;
}

// Re-export so consumers can import from either location
export { PLAN_LIMITS, getPlanLimits };

/**
 * Check if user can create a new conflict.
 * @param {object} userBilling — { plan, planStatus } from stripe/users/{userId}
 * @param {object} tenant — tenant object (org plans use tenant.plan)
 * @param {number} activeConflictCount — number of user's non-resolved conflicts
 * @returns {{ allowed: boolean, reason?: string }}
 */
function canCreateConflict(userBilling, tenant, activeConflictCount) {
  // Determine effective plan: org plan takes precedence
  const plan = (tenant && tenant.plan && tenant.id !== 'tenant-public')
    ? tenant.plan
    : userBilling?.plan;

  if (!plan) {
    return { allowed: false, reason: 'No active subscription. Please choose a plan to continue.' };
  }

  // Check subscription status
  const status = (tenant && tenant.id !== 'tenant-public')
    ? tenant.status
    : userBilling?.planStatus;

  if (status === 'cancelled') {
    return { allowed: false, reason: 'Your subscription has been cancelled. Please resubscribe to create conflicts.' };
  }
  if (status === 'past_due') {
    return { allowed: false, reason: 'Your payment is past due. Please update your payment method.' };
  }

  const limits = getPlanLimits(plan);
  if (!limits) {
    return { allowed: false, reason: 'Unknown plan. Please contact support.' };
  }

  if (activeConflictCount >= limits.maxConflicts) {
    return { allowed: false, reason: `Your ${plan} plan allows ${limits.maxConflicts} active conflict${limits.maxConflicts === 1 ? '' : 's'}. Resolve an existing conflict or upgrade to Personal+.` };
  }

  return { allowed: true };
}

/**
 * Check if user can access mentor features.
 * @param {object} userBilling — { plan, planStatus }
 * @param {object} tenant — tenant object
 * @returns {{ allowed: boolean, reason?: string }}
 */
function canAccessMentor(userBilling, tenant) {
  if (!hasCurrentMembership(userBilling, tenant)) {
    return { allowed: false, reason: 'An active Personal+ or organizational subscription is required for mentor access.' };
  }
  const plan = (tenant && tenant.plan && tenant.id !== 'tenant-public')
    ? tenant.plan
    : userBilling?.plan;

  if (!plan) {
    return { allowed: false, reason: 'No active subscription.' };
  }

  const limits = getPlanLimits(plan);
  if (!limits) {
    return { allowed: false, reason: 'Unknown plan.' };
  }

  if (!limits.mentorAccess) {
    return { allowed: false, reason: 'Mentor access requires Personal+ or an organizational plan. Upgrade to connect with mentors.' };
  }

  return { allowed: true };
}

/**
 * Check if user can hold a non-participant role (mentor, observer, global).
 * Requires Personal+, Professional, or Enterprise plan.
 * @param {object} userBilling — { plan, planStatus }
 * @param {object} tenant — tenant object
 * @returns {{ allowed: boolean, reason?: string }}
 */
function canAccessNonParticipantRole(userBilling, tenant) {
  if (!hasCurrentMembership(userBilling, tenant)) {
    return { allowed: false, reason: 'An active Personal+ or organizational membership is required for this role.' };
  }
  const plan = (tenant && tenant.plan && tenant.id !== 'tenant-public')
    ? tenant.plan
    : userBilling?.plan;

  if (!plan) {
    return { allowed: false, reason: 'No active subscription. A Personal+ or organizational plan is required.' };
  }

  const limits = getPlanLimits(plan);
  if (!limits) {
    return { allowed: false, reason: 'Unknown plan.' };
  }

  if (!limits.mentorAccess) {
    return { allowed: false, reason: 'Your current plan does not support this role. Upgrade to Personal+ or an organizational plan.' };
  }

  return { allowed: true };
}

/**
 * Check if tenant has seat capacity for a new user.
 * @param {object} tenant — tenant object with maxSeats, activeSeats
 * @returns {{ allowed: boolean, reason?: string, activeSeats: number, maxSeats: number }}
 */
function checkSeatCapacity(tenant) {
  if (!tenant) {
    return { allowed: true, activeSeats: 0, maxSeats: Infinity };
  }

  const activeSeats = tenant.activeSeats || 0;
  const maxSeats = tenant.maxSeats || Infinity;

  if (activeSeats >= maxSeats) {
    return {
      allowed: false,
      reason: `Seat limit reached (${activeSeats}/${maxSeats}). Contact your admin to add more seats or deactivate inactive users.`,
      activeSeats,
      maxSeats,
    };
  }

  return { allowed: true, activeSeats, maxSeats };
}

/**
 * Check if a subscription is in trial and return days remaining.
 * @param {object} userBilling — { planStatus, trialEndsAt }
 * @param {object} tenant — tenant object with status, trialExpiresAt
 * @returns {{ inTrial: boolean, daysRemaining: number, expired: boolean }}
 */
function getTrialStatus(userBilling, tenant) {
  const isOrg = tenant && tenant.id !== 'tenant-public' && tenant.plan;
  const status = isOrg ? tenant.status : userBilling?.planStatus;
  const trialEnd = isOrg ? tenant.trialExpiresAt : userBilling?.trialEndsAt;

  if (status !== 'trialing' || !trialEnd) {
    return { inTrial: false, daysRemaining: 0, expired: false };
  }

  const endDate = new Date(trialEnd);
  const now = new Date();
  const msRemaining = endDate.getTime() - now.getTime();
  const daysRemaining = Math.max(0, Math.ceil(msRemaining / (1000 * 60 * 60 * 24)));

  return {
    inTrial: true,
    daysRemaining,
    expired: msRemaining <= 0,
  };
}

/**
 * Get the number of days remaining in a cancelled tenant's grace period.
 * @param {object} tenant — tenant object with cancelledAt
 * @returns {number|null} — days remaining, or null if not cancelled
 */
function getTenantDaysRemaining(tenant) {
  if (!tenant || tenant.status !== 'cancelled' || !tenant.cancelledAt) return null;
  const cancelledDate = new Date(tenant.cancelledAt);
  const gracePeriodMs = (DEFAULT_CONFIG.gracePeriodDays) * 24 * 60 * 60 * 1000;
  const expiresAt = cancelledDate.getTime() + gracePeriodMs;
  const remaining = expiresAt - Date.now();
  return Math.max(0, Math.ceil(remaining / (1000 * 60 * 60 * 24)));
}

/**
 * Check if a tenant is in read-only state (cancelled or archived).
 * @param {object} tenant — tenant object
 * @returns {boolean}
 */
function isTenantReadOnly(tenant) {
  if (!tenant) return false;
  return tenant.status === 'cancelled' || tenant.status === 'archived';
}

/**
 * Unified check: is billing in a read-only state?
 * Covers both org tenant cancellation/archival AND personal plan cancellation.
 * @param {object} userBilling — { planStatus } from stripe/users/{userId}
 * @param {object} tenant — tenant object
 * @returns {{ readOnly: boolean, reason: string, canExport: boolean }}
 */
function isBillingReadOnly(userBilling, tenant) {
  // Org tenant: cancelled or archived
  if (tenant && tenant.id !== 'tenant-public') {
    if (tenant.status === 'archived') {
      return {
        readOnly: true,
        reason: 'This organization\'s account has been archived. Contact support for record requests.',
        canExport: true,
      };
    }
    if (tenant.status === 'cancelled') {
      const days = getTenantDaysRemaining(tenant);
      return {
        readOnly: true,
        reason: `Your organization's subscription has been cancelled. You have ${days != null ? days : '—'} days remaining to export your data.`,
        canExport: true,
      };
    }
  }

  // Personal plan: cancelled
  if (userBilling?.planStatus === 'cancelled') {
    return {
      readOnly: true,
      reason: 'Your subscription has ended. Resubscribe to continue editing conflicts.',
      canExport: true,
    };
  }

  return { readOnly: false, reason: '', canExport: true };
}

export {
  canCreateConflict,
  canAccessMentor,
  canAccessNonParticipantRole,
  checkSeatCapacity,
  getTrialStatus,
  getTenantDaysRemaining,
  isTenantReadOnly,
  isBillingReadOnly,
};
